import { FC, useMemo } from 'react';

import { cn } from '@eluve/components';
import { computeAudioLevel } from '@eluve/mic-utils';

interface InlineMicrophoneLevelBarsProps {
  waveformData?: number[];
  className?: string;
}

const NUM_BARS = 5;

/**
 * A minimal inline sound bars component designed to sit inline with text.
 * Shows 5 bars with active/inactive state based on audio level.
 * Matches the compact variant of MicrophoneLevelVisualization.
 */
export const InlineMicrophoneLevelBars: FC<InlineMicrophoneLevelBarsProps> = ({
  waveformData,
  className,
}) => {
  const activeBarCount = useMemo(() => {
    if (!waveformData || waveformData.length === 0) {
      return 0;
    }

    return Math.round(computeAudioLevel(waveformData) * NUM_BARS);
  }, [waveformData]);

  return (
    <div className={cn('flex items-center gap-1 px-2', className)}>
      {Array(NUM_BARS)
        .fill(0)
        .map((_, i) => (
          <div
            key={i}
            className={cn(
              'h-4 w-1.5 rounded-full transition-colors duration-100 ease-in-out',
              i < activeBarCount
                ? 'bg-bg-accent-strong-primary'
                : 'bg-border-secondary',
            )}
          />
        ))}
    </div>
  );
};
